<script setup>
import { h, ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import {
  dividirBarra,
  quantosCabem,
  PASSO_DO_ICONE,
  ordenarPorGrupo,
  comecosDeGrupo,
  emBlocos,
} from './barraQueCabe';
import {
  provideSidebarContext,
  useSidebarResize,
  useSidebarLayout,
} from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useNaoLidasDoMenu } from 'dashboard/routes/dashboard/correio/naoLidasDoMenu';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { useRoute, useRouter } from 'vue-router';
import { itemPeloCaminho } from 'dashboard/helper/itemPeloCaminho';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import TelefoneBotao from 'dashboard/components-next/telefone/TelefoneBotao.vue';
import BuscaGlobalModal from 'dashboard/components-next/busca/BuscaGlobalModal.vue';
import { useBuscaGlobal } from 'dashboard/components-next/busca/useBuscaGlobal';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { abrir: abrirBusca } = useBuscaGlobal();

// 🔴 O MARCADOR QUE DESLIZA (Victor, 10/09/2026: os ícones "sem animação
// tudo, sem vida, grotesco, não parece de um produto premium").
//
// Antes o item ativo era um QUADRADO AZUL CHEIO que aparecia e sumia: trocar
// de tela era um piscar, e vinte quadrados iguais numa fileira viravam parede.
// Agora existe UM marcador só, que escorrega da posição antiga para a nova.
// É o que dá a sensação de continuidade — a barra vira um lugar, e não vinte
// botões soltos.
//
// MEDIR, E NÃO ADIVINHAR: a posição sai de `offsetLeft`/`offsetWidth` do item
// ativo de verdade. Calcular por índice quebraria no primeiro item escondido
// por feature flag, e a barra ROLA na horizontal — o marcador vive dentro do
// mesmo container rolável, então acompanha a rolagem de graça.
const navRef = ref(null);
const cabecalhoRef = ref(null);
const logoRef = ref(null);
const direitaRef = ref(null);
const marcador = ref({ x: 0, largura: 0, visivel: false });
// O módulo aberto mostra o nome ao lado do ícone: a largura a mais que ele
// ocupa sai da conta, senão o último ícone vazaria para fora da barra.
const extraDoAtivo = ref(0);

const medirMarcador = async () => {
  await nextTick();
  const nav = navRef.value;
  const alvo = nav?.querySelector('[data-nav-ativo="1"]');
  if (!nav || !alvo) {
    marcador.value = { ...marcador.value, visivel: false };
    return;
  }
  marcador.value = {
    x: alvo.offsetLeft,
    largura: alvo.offsetWidth,
    visivel: true,
  };
  extraDoAtivo.value = Math.max(0, alvo.offsetWidth - PASSO_DO_ICONE + 6);
};

const estiloDoMarcador = computed(() => ({
  translate: `${marcador.value.x}px`,
  width: `${marcador.value.largura}px`,
  opacity: marcador.value.visivel ? 1 : 0,
}));

// A barra muda de tamanho quando o menu recolhe, quando a janela encolhe e
// quando um módulo entra por feature flag. Observar é a única forma de medir
// na hora certa — foi assim que a pílula da ficha do processo parou de medir
// zero em aba de fundo.
let observadorDaBarra = null;

// QUANTOS ÍCONES CABEM (05/10/2026) — ver `barraQueCabe.js`. O espaço da
// barra é o cabeçalho menos o logo, os botões da direita e um mínimo para a
// busca; medir a própria barra não serve, porque ela encolhe junto com o que
// mostra.
const BUSCA_MINIMA = 220;
// O fio entre grupos: 1px de traço + 4px de cada lado + o vão da barra.
const LARGURA_DO_FIO = 15;
const espacoDaBarra = ref(Infinity);
const medirCapacidade = () => {
  const cabecalho = cabecalhoRef.value;
  if (!cabecalho) return;
  const estilo = getComputedStyle(cabecalho);
  const vao = parseFloat(estilo.columnGap) || 0;
  const espaco =
    cabecalho.clientWidth -
    (parseFloat(estilo.paddingLeft) || 0) -
    (parseFloat(estilo.paddingRight) || 0) -
    (logoRef.value?.offsetWidth || 0) -
    (direitaRef.value?.offsetWidth || 0) -
    BUSCA_MINIMA -
    // logo · divisor · busca · barra · direita = quatro vãos, mais o divisor
    vao * 4 -
    1;
  espacoDaBarra.value = espaco;
};

let observadorDoCabecalho = null;

onMounted(() => {
  medirMarcador();
  medirCapacidade();
  if (typeof ResizeObserver !== 'undefined' && navRef.value) {
    observadorDaBarra = new ResizeObserver(() => medirMarcador());
    observadorDaBarra.observe(navRef.value);
  }
  if (typeof ResizeObserver !== 'undefined' && cabecalhoRef.value) {
    observadorDoCabecalho = new ResizeObserver(() => medirCapacidade());
    observadorDoCabecalho.observe(cabecalhoRef.value);
    if (direitaRef.value) observadorDoCabecalho.observe(direitaRef.value);
  }
});

onUnmounted(() => {
  observadorDaBarra?.disconnect();
  observadorDoCabecalho?.disconnect();
});

// ⌘ no Mac, Ctrl no resto — o atalho que a pessoa já aprendeu no Spotlight, no
// VS Code e no Slack. Só o RÓTULO muda aqui; quem escuta a tecla é o modal.
// Vai como LISTA e não como texto solto no template: string crua no template
// é barrada pelo lint de tradução, e com razão — o dia em que "K" precisar
// virar outra coisa, ela está aqui.
const teclasDoAtalho =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? ['⌘', 'K']
    : ['Ctrl', 'K'];

const advupIconSrc = '/brand-assets/advup-icon-ref.png';

import Button from 'dashboard/components-next/button/Button.vue';
import LogoAdvup from 'dashboard/components-next/marca/LogoAdvup.vue';
import SidebarGroup from './SidebarGroup.vue';
import InstalarApp from './InstalarApp.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import SidebarNotificationPanel from './SidebarNotificationPanel.vue';
import BotaoDeNovidades from 'dashboard/components-next/escritorio/novidades/BotaoDeNovidades.vue';
import BotaoDePendencias from 'dashboard/components-next/pendencias/BotaoDePendencias.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const { accountScopedRoute, isOnChatwootCloud } = useAccount();
const { isFeatureFlagEnabled } = usePolicy();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);

// Pedido do Victor 2026-07-16: badge vermelho (igual sino) no ícone de Chat
// Interno da barra horizontal, não só a bolinha azul discreta que já existia.
const internalChatUnread = computed(
  () => store.getters['internalChats/totalUnread'] || 0
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);
// advup — achado 2026-07-20 (missão PWA mobile do Victor): abaixo de 640px
// (mesmo corte onde a nav horizontal já vira "hidden sm:flex"), as duas
// logos sozinhas ocupavam ~304px + o cluster de busca/notificação/perfil
// (~215px, perfil com nome/e-mail visível) precisava de mais que os 375px
// de tela — busca/sino/avatar ficavam cortados fora da viewport, sem
// scroll, literalmente inacessíveis num iPhone/Android comum. Só afeta
// classes `sm:` — acima de 640px nada muda.
const isHeaderCompact = computed(() => windowWidth.value < 640);

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// Pedido do Victor 2026-09-24: a mesma bolinha vermelha no ícone "E-mail"
// quando há e-mail não lido na Entrada (naoLidasDoMenu.js). Só conta com o
// módulo ligado no escritório; trocar de escritório conta de novo.
const {
  total: correioNaoLidas,
  ligar: ligarCorreio,
  desligar: desligarCorreio,
} = useNaoLidasDoMenu();
const correioLigado = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CORREIO)
);
watch(
  [correioLigado, accountId],
  ([ligado]) => (ligado ? ligarCorreio() : desligarCorreio()),
  { immediate: true }
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

const { isHorizontal, setSidebarLayout } = useSidebarLayout();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Hover-to-expand while collapsed
const isHoverExpanded = ref(false);
const isDisplayExpanded = computed(
  () => !isEffectivelyCollapsed.value || isHoverExpanded.value
);

const onSidebarMouseEnter = () => {
  if (isEffectivelyCollapsed.value) isHoverExpanded.value = true;
};
const onSidebarMouseLeave = () => {
  isHoverExpanded.value = false;
};

const toggleCollapsed = () => {
  if (isEffectivelyCollapsed.value) snapToExpanded();
  else snapToCollapsed();
  isHoverExpanded.value = false;
};

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

const isDisplayCollapsed = computed(
  () => isEffectivelyCollapsed.value && !isHoverExpanded.value
);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isDisplayCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const getInboxUnreadCount = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);
const getLabelUnreadCount = useMapGetter(
  'conversationUnreadCounts/getLabelUnreadCount'
);
const getTeamUnreadCount = useMapGetter(
  'conversationUnreadCounts/getTeamUnreadCount'
);
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const menuApps = useMapGetter('menuApps/allMenuApps');

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  // advup — 2026-07-28: precisa estar disponível account-wide (não só na
  // tela Settings > Agent Bots) pro filtro/reatribuição de conversa saberem
  // exibir a Andie quando ela é assignee_agent_bot, não User.
  store.dispatch('agentBots/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  store.dispatch('menuApps/fetch');
});

watch(
  [accountId, hasConversationUnreadCounts],
  ([currentAccountId, isEnabled]) => {
    if (!currentAccountId) return;
    if (!isEnabled) {
      store.dispatch('conversationUnreadCounts/clear');
      return;
    }
    store.dispatch('conversationUnreadCounts/get');
  },
  { immediate: true }
);

const menuAppItems = computed(() =>
  menuApps.value.map(app => ({
    name: `menu_app_${app.id}`,
    label: app.title,
    icon: app.icon || 'i-lucide-layout-grid',
    to: accountScopedRoute('menu_app_view', { appId: app.id }),
    activeOn: ['menu_app_view'],
    featureFlag: FEATURE_FLAGS.MENU_APPS,
  }))
);

const normalizeUnreadCount = count => {
  const n = Number(count);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const sortByUnreadCount = (items, labelKey, unreadCountKey) =>
  items.slice().sort((a, b) => {
    const diff =
      normalizeUnreadCount(unreadCountKey(b)) -
      normalizeUnreadCount(unreadCountKey(a));
    if (diff !== 0) return diff;
    return labelKey(a).localeCompare(labelKey(b));
  });

const sortedInboxes = computed(() =>
  sortByUnreadCount(
    inboxes.value,
    inbox => inbox.name,
    inbox => getInboxUnreadCount.value(inbox.id)
  )
);

const sortedTeams = computed(() =>
  sortByUnreadCount(
    teams.value,
    team => team.name,
    team => getTeamUnreadCount.value(team.id)
  )
);

const sortedLabels = computed(() =>
  sortByUnreadCount(
    labels.value,
    label => label.title,
    label => getLabelUnreadCount.value(label.id)
  )
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

// advup — folha "Mais" do bottom-nav mobile (missão PWA 2026-07-20): reusa
// horizontalNavItems (mesmo array da nav de desktop) pra listar TODOS os
// módulos, não só os 4 do bottom-nav. Grupos com filhos (ex: Configurações)
// expandem inline em vez de dropdown flutuante (sem espaço pra isso no mobile).
const expandedMobileGroup = ref(null);
const toggleMobileGroup = name => {
  expandedMobileGroup.value = expandedMobileGroup.value === name ? null : name;
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const filterByFeatureFlag = items => {
  return items.filter(item => {
    // If item has a featureFlag, check if it's enabled
    if (item.featureFlag && !isFeatureFlagEnabled(item.featureFlag)) {
      return false;
    }
    // If item has children, filter them recursively
    if (item.children) {
      item.children = filterByFeatureFlag(item.children);
    }
    return true;
  });
};

const menuItems = computed(() => {
  const items = [
    {
      name: 'LegalDashboard',
      label: t('SIDEBAR.LEGAL_DASHBOARD'),
      icon: 'i-lucide-gauge',
      to: accountScopedRoute('legal_dashboard'),
      activeOn: ['legal_dashboard'],
      featureFlag: FEATURE_FLAGS.LEGAL_MODULE,
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Participating',
          label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
          activeOn: ['conversation_through_participating'],
          to: accountScopedRoute('conversation_participating'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['folder_conversations', 'conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['team_conversations', 'conversations_through_team'],
          children: sortedTeams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            badgeCount: getTeamUnreadCount.value(team.id),
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['inbox_dashboard', 'conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            badgeCount: getInboxUnreadCount.value(inbox.id),
            icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
                badgeCount: leafProps.badgeCount,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['label_conversations', 'conversations_through_label'],
          children: sortedLabels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title.replace(/-/g, ' ').toUpperCase(),
            badgeCount: getLabelUnreadCount.value(label.id),
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Pipeline',
      label: t('SIDEBAR.PIPELINE'),
      icon: 'i-lucide-kanban',
      activeOn: [
        'pipeline_index',
        'pipeline_with_funnel',
        'pipeline_board',
        'pipeline_item_detail',
        'pipeline_sequences',
        'pipeline_sequence_new',
        'pipeline_sequence_edit',
      ],
      featureFlag: FEATURE_FLAGS.PIPELINE_BOARD,
      to: accountScopedRoute('pipeline_index'),
    },
    {
      name: 'FollowUps',
      label: t('SIDEBAR.FOLLOW_UPS'),
      icon: 'i-lucide-calendar-clock',
      to: accountScopedRoute('follow_ups'),
      activeOn: ['follow_ups', 'follow_up_cadences', 'follow_up_reminders'],
      featureFlag: FEATURE_FLAGS.FOLLOW_UPS,
    },
    {
      name: 'InternalChat',
      label: t('SIDEBAR.INTERNAL_CHAT'),
      icon: 'i-lucide-messages-square',
      to: accountScopedRoute('internal_chat'),
      activeOn: ['internal_chat', 'internal_chat_detail'],
      featureFlag: FEATURE_FLAGS.INTERNAL_CHAT,
      getterKeys: { count: 'internalChats/totalUnread' },
    },
    {
      name: 'AdvupLabs',
      label: t('SIDEBAR.ADVUP_LABS'),
      icon: 'i-lucide-workflow',
      to: accountScopedRoute('advup_labs_index'),
      activeOn: ['advup_labs_index'],
      featureFlag: FEATURE_FLAGS.ADVUP_LABS,
    },
    ...menuAppItems.value,
    {
      name: 'Appointments',
      label: t('SIDEBAR.APPOINTMENTS'),
      icon: 'i-lucide-calendar-days',
      to: accountScopedRoute('appointments_calendar'),
      activeOn: ['appointments_root', 'appointments_calendar'],
      featureFlag: FEATURE_FLAGS.APPOINTMENTS_MODULE,
    },
    {
      name: 'AnaliseComerciall',
      label: t('SIDEBAR.ANALISE_COMERCIAL'),
      icon: 'i-lucide-brain-circuit',
      to: accountScopedRoute('analise_comercial_index'),
      activeOn: ['analise_comercial_index'],
      featureFlag: FEATURE_FLAGS.ANALISE_COMERCIAL,
    },
    {
      name: 'LegalProcesses',
      label: t('SIDEBAR.LEGAL_MODULE'),
      icon: 'i-lucide-scale',
      to: accountScopedRoute('legal_processes_index'),
      activeOn: [
        'legal_processes_index',
        'legal_process_show',
        'legal_processes_reports',
      ],
      featureFlag: FEATURE_FLAGS.LEGAL_MODULE,
    },
    {
      name: 'LegalClients',
      label: t('SIDEBAR.LEGAL_CLIENTS'),
      icon: 'i-lucide-users-round',
      to: accountScopedRoute('legal_clients_index'),
      activeOn: ['legal_clients_index', 'legal_client_show'],
      featureFlag: FEATURE_FLAGS.LEGAL_CLIENTS,
    },
    {
      name: 'LegalPublications',
      label: t('SIDEBAR.LEGAL_PUBLICATIONS'),
      icon: 'i-lucide-newspaper',
      to: accountScopedRoute('legal_publications_index'),
      activeOn: ['legal_publications_index'],
      featureFlag: FEATURE_FLAGS.LEGAL_PUBLICATIONS,
    },
    {
      name: 'LegalCases',
      label: t('SIDEBAR.LEGAL_CASES'),
      icon: 'i-lucide-folder-tree',
      to: accountScopedRoute('legal_cases_index'),
      activeOn: ['legal_cases_index'],
      featureFlag: FEATURE_FLAGS.LEGAL_CASES,
    },
    {
      name: 'LegalGed',
      label: t('SIDEBAR.LEGAL_GED'),
      icon: 'i-lucide-folder-open',
      to: accountScopedRoute('legal_ged_index'),
      activeOn: ['legal_ged_index'],
      featureFlag: FEATURE_FLAGS.LEGAL_GED_CENTRAL,
    },
    // Módulo Minutas (29/09/2026): substitui "Contratos". Minutas (o editor,
    // Fase 3), Modelos (a biblioteca, Fase 4), Assinaturas e Contratos.
    {
      name: 'Minutas',
      label: t('MINUTAS.MENU.GRUPO'),
      icon: 'i-lucide-file-signature',
      featureFlag: FEATURE_FLAGS.CONTRACTS,
      children: [
        {
          name: 'MinutasMinutas',
          label: t('MINUTAS.MENU.MINUTAS'),
          activeOn: ['minutas_index', 'minutas_minuta'],
          to: accountScopedRoute('minutas_index'),
        },
        {
          name: 'MinutasModelos',
          label: t('MINUTAS.MENU.MODELOS'),
          activeOn: ['minutas_modelos', 'minutas_modelo'],
          to: accountScopedRoute('minutas_modelos'),
        },
        {
          name: 'MinutasAssinaturas',
          label: t('MINUTAS.MENU.ASSINATURAS'),
          activeOn: ['minutas_assinaturas'],
          to: accountScopedRoute('minutas_assinaturas'),
        },
        {
          name: 'MinutasContratos',
          label: t('MINUTAS.MENU.CONTRATOS'),
          // O "gerar documento" sai daqui (Minutas › Contratos).
          activeOn: ['legal_contracts_index', 'legal_document_builder'],
          to: accountScopedRoute('legal_contracts_index'),
        },
      ],
    },
    {
      name: 'Correio',
      label: t('SIDEBAR.CORREIO'),
      icon: 'i-lucide-mail',
      to: accountScopedRoute('correio_index'),
      activeOn: ['correio_index'],
      featureFlag: FEATURE_FLAGS.CORREIO,
    },
    {
      name: 'CentralTelefonica',
      label: t('SIDEBAR.CENTRAL_TELEFONICA'),
      icon: 'i-lucide-phone',
      to: accountScopedRoute('central_telefonica_index'),
      activeOn: ['central_telefonica_index'],
      featureFlag: FEATURE_FLAGS.TELEPHONY,
    },
    {
      name: 'FinancialModule',
      label: t('SIDEBAR.FINANCIAL_MODULE'),
      icon: 'i-lucide-wallet',
      to: accountScopedRoute('financial_index'),
      activeOn: ['financial_index'],
      featureFlag: FEATURE_FLAGS.FINANCIAL_MODULE,
    },
    {
      name: 'Captain',
      icon: 'i-lucide-bot',
      label: t('SIDEBAR.CAPTAIN'),
      activeOn: [
        'captain_assistants_create_index',
        'captain_assistants_responses_index',
        'captain_assistants_responses_pending',
        'captain_assistants_documents_index',
        'captain_assistants_scenarios_index',
        'captain_assistants_playground_index',
        'captain_assistants_inboxes_index',
        'captain_tools_index',
        'captain_assistants_settings_index',
        'captain_assistants_guidelines_index',
        'captain_assistants_guardrails_index',
      ],
      to: accountScopedRoute('captain_assistants_create_index'),
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      to: accountScopedRoute(
        'contacts_dashboard_index',
        {},
        { page: 1, search: undefined }
      ),
      activeOn: [
        'contacts_dashboard_index',
        'contacts_edit',
        'contacts_dashboard_active',
        'contacts_dashboard_segments_index',
        'contacts_edit_segment',
        'contacts_dashboard_labels_index',
        'contacts_edit_label',
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-bar-chart-3',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
          featureFlag: FEATURE_FLAGS.SLA,
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
        {
          name: 'Lead Score',
          label: t('SIDEBAR.REPORTS_LEAD_SCORE'),
          to: accountScopedRoute('lead_score_reports'),
          featureFlag: FEATURE_FLAGS.LEAD_SCORE,
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      featureFlag: FEATURE_FLAGS.CAMPAIGNS,
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
        {
          name: 'WAHA',
          label: t('SIDEBAR.WAHA_WHATSAPP'),
          to: accountScopedRoute('campaigns_waha_index'),
        },
      ],
    },
    {
      name: 'Broadcasts',
      label: t('SIDEBAR.BROADCASTS'),
      icon: 'i-lucide-send',
      to: accountScopedRoute('broadcasts_list'),
      activeOn: ['broadcasts_list'],
      featureFlag: FEATURE_FLAGS.BROADCASTS,
    },
    {
      name: 'Portals',
      featureFlag: FEATURE_FLAGS.HELP_CENTER,
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-settings-2',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        // Os dados do escritório (nome, CNPJ, OAB, endereço, assinatura)
        // moravam em "Processos" e só se chegava por um botão das Minutas ou
        // da lista de processos (Victor, 06/10/2026: "Caminho nada a ver").
        {
          name: 'Settings Office',
          label: t('SIDEBAR.OFFICE_DATA'),
          icon: 'i-lucide-building-2',
          to: accountScopedRoute('legal_settings'),
          activeOn: ['legal_settings'],
          featureFlag: FEATURE_FLAGS.LEGAL_MODULE,
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Appointments',
          label: t('SIDEBAR.APPOINTMENTS_SETTINGS'),
          icon: 'i-lucide-handshake',
          to: accountScopedRoute('appointments_settings'),
          featureFlag: FEATURE_FLAGS.APPOINTMENTS_MODULE,
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                  'agent_capacity_policy_index',
                  'agent_capacity_policy_create',
                  'agent_capacity_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          activeOn: [
            'settings_inbox_list',
            'settings_inbox_show',
            'settings_inbox_new',
            'settings_inbox_finish',
            'settings_inboxes_page_channel',
            'settings_inboxes_add_agents',
          ],
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings WhatsApp Templates',
          label: t('SIDEBAR.WHATSAPP_TEMPLATES'),
          icon: 'i-lucide-message-circle',
          to: accountScopedRoute('whatsapp_templates_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings System Health',
          label: t('SIDEBAR.SYSTEM_HEALTH'),
          icon: 'i-lucide-activity',
          to: accountScopedRoute('system_health'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
          // 07/10/2026: com o recurso desligado a tela era só a propaganda de
          // plano do Chatwoot ("Atualizar agora"). Sem recurso, sem item.
          featureFlag: FEATURE_FLAGS.CUSTOM_ROLES,
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
          featureFlag: FEATURE_FLAGS.SLA,
        },
        {
          name: 'Settings Lead Score',
          label: t('SIDEBAR.LEAD_SCORE'),
          icon: 'i-lucide-trending-up',
          to: accountScopedRoute('lead_score_list'),
          featureFlag: FEATURE_FLAGS.LEAD_SCORE,
        },
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Correio',
          label: t('SIDEBAR.CORREIO'),
          icon: 'i-lucide-mail',
          to: accountScopedRoute('correio_servidores'),
          featureFlag: FEATURE_FLAGS.CORREIO,
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        // advup: item "Cobrança" escondido — é o billing do Chatwoot Cloud (morto no
        // self-hosted, redireciona pro /dashboard). Reativar quando houver billing
        // próprio do SaaS (Asaas/Stripe). Ver memory project_advup_billing.
        // {
        //   name: 'Settings Billing',
        //   label: t('SIDEBAR.BILLING'),
        //   icon: 'i-lucide-credit-card',
        //   to: accountScopedRoute('billing_settings_index'),
        // },
      ],
    },
  ];

  return filterByFeatureFlag(items);
});

// Em grupos (`ordenarPorGrupo`, 08/10/2026): a barra, o "Mais", a folha do
// celular e a busca leem esta mesma lista, então todos andam na mesma ordem.
const horizontalNavItems = computed(() =>
  ordenarPorGrupo(
    menuItems.value
      .map(item => {
        const destino = item.to ?? item.children?.find(c => c.to)?.to;

        return {
          name: item.name,
          label: item.label,
          icon: item.icon,
          to: destino,
          children:
            item.name === 'Conversation'
              ? []
              : (item.children?.filter(c => c.to && c.label) ?? []),
          // 🔴 A ROTA DE DESTINO DO PRÓPRIO ITEM ENTRA NA LISTA.
          //
          // Victor, 10/09/2026: "quando eu estou em cima de conversa aí não
          // aparece o balãozinho azul em cima igual nas outras opções do menu".
          //
          // Para um item com submenu, a lista de ativação era montada só a
          // partir dos FILHOS. O de Conversas herdava oito rotas, e nenhuma
          // delas era `home` — que é exatamente onde o item leva ao ser clicado.
          // Resultado: o ícone ficava apagado justamente na tela mais usada do
          // produto. O bloco azul antigo também não acendia; o marcador novo só
          // tornou a falha impossível de ignorar.
          //
          // Clicar no item leva ao destino, logo estar no destino é estar nele.
          // Vale para todos os itens com submenu, não só para Conversas.
          activeOn: item.activeOn ?? [
            ...(item.children?.flatMap(c => c.activeOn ?? []) ?? []),
            ...(destino?.name ? [destino.name] : []),
          ],
          // Os endereços do item e de TODOS os filhos (inclusive os que a barra
          // não mostra, como os de Conversas): reserva de `itemPeloCaminho`.
          caminhos: [item.to, ...(item.children ?? []).map(c => c.to)]
            .filter(Boolean)
            .map(to => {
              try {
                return router.resolve(to).path;
              } catch {
                return null;
              }
            })
            .filter(Boolean),
        };
      })
      .filter(item => item.icon && (item.to || item.children?.length))
  )
);

const navTooltip = ref({ visible: false, text: '', x: 0, y: 0 });
const showNavTooltip = (item, event) => {
  // O aberto já escreve o nome na própria barra: dica repetiria.
  if (event.currentTarget.dataset.navAtivo === '1') return;
  const rect = event.currentTarget.getBoundingClientRect();
  navTooltip.value = {
    visible: true,
    text: item.label,
    x: rect.left + rect.width / 2,
    y: rect.bottom + 8,
  };
};
const hideNavTooltip = () => {
  navTooltip.value.visible = false;
};

const activeDropdown = ref(null);
const dropdownPos = ref({ x: 0, y: 0 });
const toggleHorizDropdown = (item, event) => {
  if (activeDropdown.value === item.name) {
    activeDropdown.value = null;
  } else {
    const rect = event.currentTarget.getBoundingClientRect();
    // O "Mais" fica no fim da barra: o painel não pode passar da tela.
    dropdownPos.value = {
      x: Math.min(rect.left, window.innerWidth - 260),
      y: rect.bottom + 6,
    };
    activeDropdown.value = item.name;
    hideNavTooltip();
  }
};
const closeHorizDropdown = () => {
  activeDropdown.value = null;
};
// 🔴 O PAI ACENDE QUANDO O FILHO ESTÁ ABERTO (Victor, 10/09/2026: "quando eu
// estou em cima de conversa aí não aparece o balãozinho azul em cima igual
// nas outras opções do menu").
//
// Itens com submenu — Conversas, Relatórios, Campanhas, Central de Ajuda e
// Configurações — não têm `activeOn` PRÓPRIO: quem tem são os filhos. Então
// `isHorizActive` devolvia `undefined` e o pai nunca acendia. O defeito é
// anterior ao marcador novo; o bloco azul antigo também não acendia nesses
// cinco. O que mudou foi ficar evidente, porque agora o marcador é um só e a
// falta dele salta aos olhos.
const casaPeloNome = item => {
  const name = route.name || '';
  const casa = alvo => alvo?.some(a => name.includes(a));
  return Boolean(
    casa(item.activeOn) || item.children?.some(f => casa(f.activeOn))
  );
};

// Nenhum item listou o nome desta rota: acende o módulo pelo começo do
// endereço (`helper/itemPeloCaminho.js` — a Agenda, Configurações,
// Relatórios e o Lumos tinham dezenas de subtelas com a barra apagada).
const ativoPeloCaminho = computed(() => {
  if (horizontalNavItems.value.some(casaPeloNome)) return null;
  return itemPeloCaminho(
    route.path,
    horizontalNavItems.value.map(item => ({
      nome: item.name,
      caminhos: item.caminhos,
    }))
  );
});

const isHorizActive = item =>
  casaPeloNome(item) || item.name === ativoPeloCaminho.value;

// 🔴 MEDIR QUANDO O ATIVO MUDA, E NÃO SÓ AO MONTAR.
//
// A primeira versão media no `onMounted` e a cada troca de nome de rota. Isso
// deixava um buraco: os módulos da barra entram por feature flag, que chega
// DEPOIS da montagem. Quando isso acontecia, a medição já tinha rodado com a
// barra vazia, ninguém mandava medir de novo, e o marcador ficava invisível
// até a pessoa trocar de tela. Medido na tela: em `/financeiro` recarregado,
// o item ativo existia no DOM e o marcador estava com opacidade 0.
//
// Agora o gatilho é um valor REATIVO — quem está ativo — em vez do ciclo de
// vida. O DOM continua sendo consultado só para a geometria.
const barraDividida = computed(() => {
  const itens = horizontalNavItems.value;
  const ehAtivo = item => isHorizActive(item);
  const cabem = desconto =>
    quantosCabem(espacoDaBarra.value - extraDoAtivo.value - desconto);
  // Duas passadas: os fios dependem de quais grupos ficaram à vista, e a
  // primeira conta diz quais são.
  const primeira = dividirBarra(itens, cabem(0), ehAtivo);
  const fios = comecosDeGrupo(primeira.visiveis).size;
  return fios
    ? dividirBarra(itens, cabem(fios * LARGURA_DO_FIO), ehAtivo)
    : primeira;
});
const comecosVisiveis = computed(() =>
  comecosDeGrupo(barraDividida.value.visiveis)
);
// O "Mais" em blocos com título, na mesma ordem da barra.
const blocosDoMais = computed(() => emBlocos(barraDividida.value.noMais));
const blocosDoCelular = computed(() => emBlocos(horizontalNavItems.value));
const rotuloDoGrupo = computed(() => ({
  inicio: t('SIDEBAR.GRUPOS.INICIO'),
  atendimento: t('SIDEBAR.GRUPOS.ATENDIMENTO'),
  juridico: t('SIDEBAR.GRUPOS.JURIDICO'),
  gestao: t('SIDEBAR.GRUPOS.GESTAO'),
  ferramentas: t('SIDEBAR.GRUPOS.FERRAMENTAS'),
}));
const itensNoMais = computed(() => barraDividida.value.noMais);
const MAIS = '__mais';

const itemAtivoDaBarra = computed(
  () => horizontalNavItems.value.find(item => isHorizActive(item))?.name || null
);

watch(itemAtivoDaBarra, () => medirMarcador());
watch(
  () => horizontalNavItems.value.length,
  () => medirMarcador()
);
watch(
  () => barraDividida.value.visiveis.length,
  () => medirMarcador()
);
</script>

<template>
  <!-- HORIZONTAL TOP BAR MODE -->
  <!-- advup PWA mobile (2026-07-27) — REGRESSÃO REAL achada pelo Victor em
       device físico: viewport-fit=cover (Fase 1.1) fez a barra do topo
       ficar debaixo do relógio/ícones do sistema no iPhone, porque só o
       bottom nav tinha ganhado safe-area, não este header. `advup-topbar-safe-area`
       usa env(safe-area-inset-top) — resolve pra 0px em qualquer tela sem
       notch (todo desktop, a maioria dos Android), então é seguro deixar
       sem gate de breakpoint. -->
  <!-- A BARRA SUPERIOR usa a escala navy da marca.
       Ela e escura NOS DOIS TEMAS de proposito — e a assinatura do produto.
       Mas os tons vinham cravados (`#0f172a`, `#1e293b`, `#94a3b8`,
       `#e2e8f0`, `#2563eb`), e a escala ja existia: `.sidebar-dark` em
       `_next-colors.scss` redefine slate/superficie/borda em navy advup. Ela
       estava escrita e sem uso — a mesma coisa que aconteceu na lateral do
       chat interno. Com a classe, os treze tons vem de um lugar so. -->
  <header
    v-if="isHorizontal"
    ref="cabecalhoRef"
    class="advup-topbar-safe-area sidebar-dark bg-n-background flex items-center min-h-20 w-full flex-shrink-0 border-b border-n-weak px-4 gap-3 relative z-50"
  >
    <!-- A logo vetorial montando-se (03/11/2026, pedido do Victor: o logo
         animado do UpChat também no AdvUp). Toca ao abrir; o mouse repete. -->
    <span ref="logoRef" class="flex-shrink-0 flex items-center">
      <LogoAdvup altura="3.5rem" compacto sobre-escuro />
    </span>
    <div class="hidden sm:block w-px h-6 bg-white/20 flex-shrink-0" />

    <!-- 🔴 A BUSCA MUDOU DE LUGAR E DE NATUREZA (Victor, 10/09/2026).
         Antes era uma lupa espremida entre o sino e o avatar, e clicar nela
         TROCAVA DE TELA para a busca de conversas do Chatwoot — que não sabe
         nada de cliente, pasta nem processo. "Acho muito feio e layout
         horrível, e além de tudo não tem muita integração com nosso sistema".
         Agora é um campo no vão que sobrava depois do logo, e abre um modal
         sobre a tela em que a pessoa já está. Ver `BuscaGlobalModal`. -->
    <!-- O vão entre o logo e os ícones é do campo de busca, e ele fica no MEIO
         desse vão — antes ficava encostado no divisor do logo, com todo o
         espaço sobrando de um lado só. Victor, 11/09/2026: "consegue
         centralizar mais aí entre a logo e o ícone?" -->
    <div class="advup-busca-vao">
      <button
        type="button"
        class="advup-busca-gatilho"
        :title="t('BUSCA_GLOBAL.ABRIR_TITULO')"
        @click="abrirBusca()"
      >
        <span class="advup-busca-gatilho__lupa-fundo" aria-hidden="true">
          <span class="i-lucide-search advup-busca-gatilho__lupa" />
        </span>
        <span class="advup-busca-gatilho__txt">
          {{ t('BUSCA_GLOBAL.PLACEHOLDER') }}
        </span>
        <span class="advup-busca-gatilho__atalho" aria-hidden="true">
          <span v-for="tecla in teclasDoAtalho" :key="tecla">{{ tecla }}</span>
        </span>
      </button>
    </div>

    <nav
      ref="navRef"
      class="advup-nav hidden sm:flex items-center ml-auto flex-none overflow-x-auto no-scrollbar py-1.5"
    >
      <!-- Um marcador só para a barra toda: ele ESCORREGA do item antigo para
           o novo. Fica atrás dos ícones (`z-0`) e dentro do container rolável,
           então acompanha a rolagem sem cálculo nenhum. -->
      <span
        class="advup-nav__marca"
        :style="estiloDoMarcador"
        aria-hidden="true"
      />
      <template v-for="item in barraDividida.visiveis" :key="item.name">
        <!-- O fio entre grupos (atendimento · jurídico · gestão): a fila de
             ícones deixa de ser uma parede só. Ver `barraQueCabe.js`. -->
        <span
          v-if="comecosVisiveis.has(item.name)"
          class="advup-nav__fio"
          aria-hidden="true"
        />
        <!-- Items with children: dropdown button -->
        <button
          v-if="item.children.length"
          class="advup-nav__item"
          :class="{
            'advup-nav__item--ativo':
              isHorizActive(item) || activeDropdown === item.name,
            'advup-nav__item--com-rotulo': isHorizActive(item),
          }"
          :data-nav-ativo="isHorizActive(item) ? '1' : '0'"
          :aria-label="item.label"
          :aria-expanded="activeDropdown === item.name"
          @mouseenter="showNavTooltip(item, $event)"
          @mouseleave="hideNavTooltip"
          @click="toggleHorizDropdown(item, $event)"
        >
          <span :class="item.icon" class="advup-nav__icone" />
          <span v-if="isHorizActive(item)" class="advup-nav__rotulo">
            {{ item.label }}
          </span>
          <span
            v-if="isHorizActive(item)"
            class="advup-nav__chevron i-lucide-chevron-down"
            aria-hidden="true"
          />
          <!-- O chevron diz que este abre mais coisas, em vez de a pessoa
               descobrir clicando. -->
          <span class="advup-nav__seta" aria-hidden="true" />
        </button>
        <!-- Items without children: direct navigation -->
        <RouterLink
          v-else
          :to="item.to"
          class="advup-nav__item"
          :class="{
            'advup-nav__item--ativo advup-nav__item--com-rotulo':
              isHorizActive(item),
          }"
          :data-nav-ativo="isHorizActive(item) ? '1' : '0'"
          :aria-label="item.label"
          :aria-current="isHorizActive(item) ? 'page' : undefined"
          @mouseenter="showNavTooltip(item, $event)"
          @mouseleave="hideNavTooltip"
        >
          <span :class="item.icon" class="advup-nav__icone" />
          <!-- O módulo aberto diz o nome: a pessoa sabe onde está sem passar
               o mouse. Os outros seguem só com o ícone e a dica. -->
          <span v-if="isHorizActive(item)" class="advup-nav__rotulo">
            {{ item.label }}
          </span>
          <span
            v-if="item.name === 'InternalChat' && internalChatUnread > 0"
            class="advup-nav__aviso"
          >
            {{ internalChatUnread < 100 ? internalChatUnread : '99+' }}
          </span>
          <span
            v-if="item.name === 'Correio' && correioNaoLidas > 0"
            class="advup-nav__aviso"
          >
            {{ correioNaoLidas < 100 ? correioNaoLidas : '99+' }}
          </span>
        </RouterLink>
      </template>
      <!-- O "Mais": os módulos que não couberam, com nome. -->
      <button
        v-if="itensNoMais.length"
        type="button"
        class="advup-nav__item"
        :class="{ 'advup-nav__item--ativo': activeDropdown === MAIS }"
        :aria-label="t('SIDEBAR.MAIS_MODULOS')"
        :aria-expanded="activeDropdown === MAIS"
        data-nav-ativo="0"
        @mouseenter="
          showNavTooltip({ label: t('SIDEBAR.MAIS_MODULOS') }, $event)
        "
        @mouseleave="hideNavTooltip"
        @click="toggleHorizDropdown({ name: MAIS }, $event)"
      >
        <span class="i-lucide-ellipsis advup-nav__icone" />
        <span
          v-if="
            itensNoMais.some(
              i =>
                (i.name === 'InternalChat' && internalChatUnread > 0) ||
                (i.name === 'Correio' && correioNaoLidas > 0)
            )
          "
          class="advup-nav__aviso advup-nav__aviso--ponto"
        />
      </button>
    </nav>
    <Teleport to="body">
      <!-- A etiqueta sobe um fio ao aparecer, em vez de piscar no lugar. É o
           mesmo movimento do resto da barra, curto o bastante para não
           atrasar quem só está passando o mouse. -->
      <Transition name="advup-dica">
        <div
          v-if="navTooltip.visible"
          class="advup-nav__dica"
          :style="{ top: navTooltip.y + 'px', left: navTooltip.x + 'px' }"
        >
          {{ navTooltip.text }}
        </div>
      </Transition>
    </Teleport>
    <Teleport to="body">
      <!-- Invisible overlay to close dropdown on outside click -->
      <div
        v-if="activeDropdown"
        class="fixed inset-0 z-[99990]"
        @click="closeHorizDropdown"
      />
      <!-- Dropdown panel -->
      <div
        v-if="activeDropdown === MAIS"
        class="advup-mais fixed z-[99991] bg-n-solid-1 rounded-xl shadow-xl border border-n-weak py-1.5 min-w-[240px] max-h-[70vh] overflow-y-auto"
        :style="{ top: dropdownPos.y + 'px', left: dropdownPos.x + 'px' }"
      >
        <div
          v-for="bloco in blocosDoMais"
          :key="bloco.grupo"
          class="advup-mais__bloco"
        >
          <p class="advup-mais__grupo-tit">
            {{ rotuloDoGrupo[bloco.grupo] }}
          </p>
          <template v-for="item in bloco.itens" :key="item.name">
            <RouterLink
              v-if="!item.children.length"
              :to="item.to"
              class="advup-mais__item"
              @click="closeHorizDropdown"
            >
              <span :class="item.icon" class="advup-mais__icone" />
              <span class="advup-mais__rotulo">{{ item.label }}</span>
              <span
                v-if="item.name === 'InternalChat' && internalChatUnread > 0"
                class="advup-mais__aviso"
              >
                {{ internalChatUnread < 100 ? internalChatUnread : '99+' }}
              </span>
              <span
                v-if="item.name === 'Correio' && correioNaoLidas > 0"
                class="advup-mais__aviso"
              >
                {{ correioNaoLidas < 100 ? correioNaoLidas : '99+' }}
              </span>
            </RouterLink>
            <div v-else class="advup-mais__grupo">
              <span class="advup-mais__item advup-mais__item--titulo">
                <span :class="item.icon" class="advup-mais__icone" />
                <span class="advup-mais__rotulo">{{ item.label }}</span>
              </span>
              <RouterLink
                v-for="child in item.children"
                :key="child.name"
                :to="child.to"
                class="advup-mais__item advup-mais__item--filho"
                @click="closeHorizDropdown"
              >
                {{ child.label }}
              </RouterLink>
            </div>
          </template>
        </div>
      </div>
      <div
        v-else-if="activeDropdown"
        class="fixed z-[99991] bg-n-solid-1 rounded-xl shadow-xl border border-n-weak py-1.5 min-w-[200px]"
        :style="{ top: dropdownPos.y + 'px', left: dropdownPos.x + 'px' }"
      >
        <RouterLink
          v-for="child in horizontalNavItems.find(
            i => i.name === activeDropdown
          )?.children ?? []"
          :key="child.name"
          :to="child.to"
          class="flex items-center gap-2.5 px-4 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
          @click="closeHorizDropdown"
        >
          {{ child.label }}
        </RouterLink>
      </div>
    </Teleport>
    <div
      ref="direitaRef"
      class="flex items-center gap-1 flex-shrink-0 ml-auto sm:ml-0"
    >
      <!-- A lupa saiu daqui: virou o campo de busca lá do começo da barra. -->
      <TelefoneBotao />
      <!-- O que espera uma DECISÃO (18/10/2026), separado do sininho. -->
      <BotaoDePendencias />
      <!-- As novidades do produto, separadas do sininho (22/10/2026). -->
      <BotaoDeNovidades />
      <SidebarNotificationPanel />
      <SidebarProfileMenu
        placement="bottom"
        inverted
        :is-collapsed="isHeaderCompact"
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </div>
  </header>

  <!-- VERTICAL MODE (default) -->
  <aside
    v-else
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
        'shadow-xl z-50': isHoverExpanded,
      },
    ]"
    :style="
      isMobile
        ? undefined
        : { width: isHoverExpanded ? '200px' : `${sidebarWidth}px` }
    "
    @mouseenter="onSidebarMouseEnter"
    @mouseleave="onSidebarMouseLeave"
  >
    <section
      class="grid"
      :class="!isDisplayExpanded ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': !isDisplayExpanded,
          'px-2': isDisplayExpanded,
        }"
      >
        <template v-if="!isDisplayExpanded">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div class="grid flex-shrink-0 place-content-center size-6">
            <img
              :src="advupIconSrc"
              alt="advup"
              class="size-5 object-contain rounded"
            />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="!isDisplayExpanded ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="isDisplayExpanded"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align-position="right" @close="onComposeClose">
          <template #trigger="{ toggle, isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                !isDisplayExpanded
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
              @click="onComposeOpen(toggle)"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="!isDisplayExpanded ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': !isDisplayExpanded }"
      >
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud && !isACustomBrandedInstance && isDisplayExpanded
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud && !isACustomBrandedInstance && !isDisplayExpanded
        "
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-1 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="
          isDisplayExpanded
            ? 'justify-between'
            : 'flex-col justify-center items-center'
        "
      >
        <BotaoDePendencias v-if="!isDisplayExpanded" />
        <BotaoDeNovidades v-if="!isDisplayExpanded" />
        <SidebarNotificationPanel v-if="!isDisplayExpanded" />
        <SidebarProfileMenu
          :is-collapsed="!isDisplayExpanded"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
        <!-- advup: botão re-expandir no modo colapsado (toggle do expandido fica escondido) -->
        <Button
          v-if="!isDisplayExpanded"
          icon="i-lucide-panel-left-open"
          color="slate"
          size="sm"
          class="!size-7"
          :title="t('SIDEBAR.EXPAND_MENU')"
          @click="toggleCollapsed"
        />
        <div
          v-if="isDisplayExpanded"
          class="flex items-center gap-0.5 flex-shrink-0"
        >
          <BotaoDePendencias />
          <BotaoDeNovidades />
          <SidebarNotificationPanel />
          <Button
            icon="i-lucide-panel-top"
            color="slate"
            size="sm"
            class="!size-7"
            @click="setSidebarLayout('horizontal')"
          />
          <Button
            :icon="
              isEffectivelyCollapsed
                ? 'i-lucide-panel-left-open'
                : 'i-lucide-panel-left-close'
            "
            color="slate"
            size="sm"
            class="!size-7"
            @click="toggleCollapsed"
          />
        </div>
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>

  <!-- MOBILE "MAIS" SHEET — lista TODOS os módulos (bottom-nav só cobre 4) -->
  <Teleport to="body">
    <div
      v-if="isHorizontal && isMobileSidebarOpen"
      class="sm:hidden fixed inset-0 z-[99980] flex flex-col justify-end"
    >
      <div class="absolute inset-0 bg-black/50" @click="closeMobileSidebar" />
      <div
        class="relative bg-n-solid-1 rounded-t-2xl max-h-[75vh] flex flex-col shadow-2xl mb-16"
      >
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak flex-shrink-0"
        >
          <span class="text-sm font-semibold text-n-slate-12">{{
            t('SIDEBAR.MENU')
          }}</span>
          <button
            class="size-8 inline-flex items-center justify-center rounded-full hover:bg-n-alpha-1 text-n-slate-11"
            :aria-label="t('SIDEBAR.FECHAR_MENU')"
            @click="closeMobileSidebar"
          >
            <span class="i-lucide-x size-[18px]" />
          </button>
        </div>
        <div class="overflow-y-auto py-2 pb-[env(safe-area-inset-bottom)]">
          <InstalarApp />
          <!-- Em grupos e em ladrilhos (08/10/2026): eram vinte linhas
               iguais numa lista que rolava, com o módulo aberto apagado.
               Três ladrilhos por linha cabem na folha sem rolar, e o aberto
               acende como na barra do computador. -->
          <section
            v-for="bloco in blocosDoCelular"
            :key="'mobile-bloco-' + bloco.grupo"
            class="mais-cel__bloco"
          >
            <!-- "Início" sozinho não precisa de título que repita o nome. -->
            <p v-if="bloco.grupo !== 'inicio'" class="mais-cel__tit">
              {{ rotuloDoGrupo[bloco.grupo] }}
            </p>
            <div class="mais-cel__grade">
              <template
                v-for="item in bloco.itens"
                :key="'mobile-nav-' + item.name"
              >
                <RouterLink
                  v-if="!item.children.length"
                  :to="item.to"
                  class="mais-cel__item"
                  :class="{ 'mais-cel__item--ativo': isHorizActive(item) }"
                  :aria-current="isHorizActive(item) ? 'page' : undefined"
                  @click="closeMobileSidebar"
                >
                  <span class="mais-cel__ladrilho">
                    <span :class="item.icon" class="mais-cel__icone" />
                  </span>
                  <span class="mais-cel__nome">{{ item.label }}</span>
                </RouterLink>
                <button
                  v-else
                  type="button"
                  class="mais-cel__item"
                  :class="{
                    'mais-cel__item--ativo': isHorizActive(item),
                    'mais-cel__item--aberto': expandedMobileGroup === item.name,
                  }"
                  :aria-expanded="expandedMobileGroup === item.name"
                  @click="toggleMobileGroup(item.name)"
                >
                  <span class="mais-cel__ladrilho">
                    <span :class="item.icon" class="mais-cel__icone" />
                    <span class="mais-cel__seta i-lucide-chevron-down" />
                  </span>
                  <span class="mais-cel__nome">{{ item.label }}</span>
                </button>
                <div
                  v-if="
                    item.children.length && expandedMobileGroup === item.name
                  "
                  class="mais-cel__filhos"
                >
                  <RouterLink
                    v-for="child in item.children"
                    :key="child.name"
                    :to="child.to"
                    class="mais-cel__filho"
                    @click="closeMobileSidebar"
                  >
                    {{ child.label }}
                  </RouterLink>
                </div>
              </template>
            </div>
          </section>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Montado UMA vez, aqui: a barra existe em toda tela do painel, então o
       ⌘K funciona em qualquer lugar sem cada rota montar a sua cópia.
       Os destinos são os MESMOS que desenham os ícones da barra — segunda
       lista de "para onde dá para ir" nasceria desatualizada. -->
  <BuscaGlobalModal :destinos="horizontalNavItems" />
</template>

<style scoped>
.advup-topbar-safe-area {
  padding-top: env(safe-area-inset-top);
  /* Medido 25/09/2026 (iPhone 15 simulado, área segura 59 px): o min-h-20
     (80 px) INCLUÍA o recuo do relógio — sobravam 45 px para uma logo de 44 e
     ela encostava no relógio ("cortando", Victor). A altura mínima é a barra
     MAIS a área segura. */
  min-height: calc(var(--advup-altura-topo) + env(safe-area-inset-top));
}

/* ── a folha "Mais" do celular ─────────────────────────────────────── */
.mais-cel__bloco {
  padding: 4px 10px 8px;
}

.mais-cel__bloco + .mais-cel__bloco {
  border-top: 1px solid rgb(var(--slate-4));
}

.mais-cel__tit {
  margin: 0;
  padding: 6px 6px 4px;
  color: rgb(var(--slate-11));
  font-size: 12px;
  font-weight: 600;
}

.mais-cel__grade {
  display: grid;
  grid-auto-flow: row dense;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2px;
}

.mais-cel__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding: 6px 2px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: rgb(var(--slate-12));
  transition:
    background-color 0.16s ease,
    transform 0.08s ease;
}

.mais-cel__item:active {
  transform: scale(0.97);
  background: rgb(var(--slate-3));
}

.mais-cel__ladrilho {
  position: relative;
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-11));
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.mais-cel__icone {
  width: 20px;
  height: 20px;
}

/* Abre subtelas (Minutas, Relatórios): o chevron no canto avisa antes. */
.mais-cel__seta {
  position: absolute;
  right: -3px;
  bottom: -3px;
  width: 16px;
  height: 16px;
  padding: 2px;
  border-radius: 999px;
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-11));
  transition: rotate 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.mais-cel__item--aberto .mais-cel__seta {
  rotate: 180deg;
}

.mais-cel__nome {
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.25;
  text-align: center;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.mais-cel__item--ativo .mais-cel__ladrilho {
  background: rgb(var(--blue-9));
  color: rgb(255 255 255);
  box-shadow: 0 6px 14px -6px rgb(var(--blue-9) / 0.7);
}

.mais-cel__item--ativo .mais-cel__nome {
  color: rgb(var(--blue-11));
  font-weight: 600;
}

/* As subtelas ocupam a linha inteira, logo abaixo do ladrilho. */
.mais-cel__filhos {
  display: flex;
  flex-wrap: wrap;
  grid-column: 1 / -1;
  gap: 6px;
  padding: 8px;
  border-radius: 12px;
  background: rgb(var(--slate-2));
  animation: mais-cel-abre 0.22s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
}

@keyframes mais-cel-abre {
  from {
    opacity: 0;
    translate: 0 -4px;
  }
}

.mais-cel__filho {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgb(var(--solid-1));
  box-shadow: inset 0 0 0 1px rgb(var(--slate-5));
  color: rgb(var(--slate-12));
  font-size: 13px;
}

.mais-cel__filho:active {
  background: rgb(var(--slate-3));
}

/* ══ A BARRA DE ÍCONES ═══════════════════════════════════════════════════
   Victor, 10/09/2026: "muito fraco, muito sem animação, sem vida, grotesco,
   não parece de um produto premium".

   O que havia: vinte quadrados iguais, e o ativo virava um BLOCO AZUL CHEIO
   que aparecia e sumia. Três problemas de uma vez — o azul saturado competia
   com o conteúdo da tela, trocar de seção era um piscar sem continuidade, e
   o hover só trocava a cor de fundo, sem dizer que aquilo se aperta.

   O desenho agora: um marcador que ESCORREGA, ícone que sobe um fio no
   hover, e afundamento no clique. Movimento curto (0,16s a 0,34s) — passou
   disso vira enfeite e atrasa quem já sabe onde quer ir. */
.advup-nav {
  position: relative;
  gap: 6px;
}

/* O marcador. `translate` e `width` separados do `transform` de propósito: a
   pílula da ficha do processo já quebrou quando duas animações disputaram a
   mesma propriedade. */
.advup-nav__marca {
  position: absolute;
  top: 50%;
  left: 0;
  z-index: 0;
  height: 40px;
  margin-top: -20px;
  border-radius: 12px;
  background: linear-gradient(
    180deg,
    rgb(var(--blue-9) / 0.28),
    rgb(var(--blue-9) / 0.16)
  );
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 14%),
    0 0 0 1px rgb(var(--blue-9) / 0.35),
    0 6px 16px -6px rgb(var(--blue-9) / 0.55);
  pointer-events: none;
  transition:
    translate 0.34s cubic-bezier(0.22, 1, 0.36, 1),
    width 0.34s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.2s ease;
}

.advup-nav__item {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  color: rgb(var(--slate-10));
  transition:
    color 0.16s ease,
    background-color 0.16s ease;
}
.advup-nav__item:hover {
  background: rgb(255 255 255 / 8%);
  color: rgb(var(--slate-12));
}
/* Afunda no clique: é o retorno físico que faltava para o ícone parecer um
   botão de verdade, e não uma figura. */
.advup-nav__item:active .advup-nav__icone {
  scale: 0.9;
}
.advup-nav__item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgb(var(--blue-9));
}
.advup-nav__item--ativo {
  color: rgb(255 255 255);
}
.advup-nav__item--ativo:hover {
  background: transparent;
}

.advup-nav__icone {
  width: 1.125rem;
  height: 1.125rem;
  transition:
    translate 0.16s cubic-bezier(0.2, 0.9, 0.3, 1),
    scale 0.12s ease;
}
/* Sobe UM pixel. Dois já parece salto, e vinte ícones saltando é circo. */
.advup-nav__item:hover .advup-nav__icone {
  translate: 0 -1px;
}
.advup-nav__item--ativo .advup-nav__icone {
  filter: drop-shadow(0 1px 3px rgb(var(--blue-9) / 0.6));
}

/* O MÓDULO ABERTO DIZ O NOME (08/10/2026). Victor mandou a foto da barra:
   "uma fila longa de ícones sem rótulo". O marcador já escorregava até o
   item; agora o item cresce para caber o nome, e o marcador cresce junto
   (ele mede `offsetWidth`). Só o aberto: dezessete nomes não cabem em
   1440px, e o resto continua com a dica. */
.advup-nav__item--com-rotulo {
  gap: 8px;
  width: auto;
  padding: 0 14px 0 11px;
}

.advup-nav__rotulo {
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  animation: advup-nav-rotulo 0.24s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
}

@keyframes advup-nav-rotulo {
  from {
    opacity: 0;
    translate: -4px 0;
  }
}

/* Com o nome à vista, o "abre mais coisas" vira chevron depois do nome — o
   pontinho embaixo ficava solto sob o texto. */
.advup-nav__item--com-rotulo .advup-nav__seta {
  display: none;
}

.advup-nav__chevron {
  width: 14px;
  height: 14px;
  margin-left: -3px;
  opacity: 0.7;
}

/* O fio entre grupos: atendimento · jurídico · gestão · ferramentas. */
.advup-nav__fio {
  flex: none;
  width: 1px;
  height: 20px;
  margin: 0 4px;
  border-radius: 1px;
  background: rgb(255 255 255 / 14%);
}

/* Quem abre submenu avisa antes, com um pontinho embaixo. Descobrir clicando
   é o tipo de coisa que faz a pessoa achar a barra "grotesca". */
.advup-nav__seta {
  position: absolute;
  bottom: 5px;
  left: 50%;
  width: 3px;
  height: 3px;
  margin-left: -1.5px;
  border-radius: 999px;
  background: currentcolor;
  opacity: 0.45;
  transition: opacity 0.16s ease;
}
.advup-nav__item:hover .advup-nav__seta,
.advup-nav__item--ativo .advup-nav__seta {
  opacity: 0.9;
}

.advup-nav__aviso {
  position: absolute;
  top: -2px;
  right: -3px;
  display: grid;
  place-items: center;
  min-width: 16px;
  min-height: 16px;
  padding: 0 4px;
  border: 2px solid rgb(var(--color-background));
  border-radius: 999px;
  background: rgb(var(--ruby-9));
  color: rgb(var(--ruby-2));
  font-size: 9px;
  font-weight: 600;
  line-height: 1;
  pointer-events: none;
}

/* Sem número: só avisa que há algo não lido dentro do "Mais". */
.advup-nav__aviso--ponto {
  top: 4px;
  right: 4px;
  min-width: 10px;
  min-height: 10px;
  padding: 0;
}

/* ── o painel do "Mais" ────────────────────────────────────────────── */
.advup-mais__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  color: rgb(var(--slate-12));
  font-size: 14px;
  transition: background-color 0.12s ease;
}

.advup-mais__item:hover {
  background: rgb(var(--slate-3));
}

/* O módulo com subtelas (Minutas, Relatórios) é um item como os outros,
   só que não leva a lugar nenhum sozinho: as subtelas ficam embaixo. Era
   título em CAIXA ALTA espaçada — anti-padrão do guia (§9). */
.advup-mais__item--titulo {
  padding-bottom: 4px;
  font-weight: 500;
}

.advup-mais__item--titulo:hover {
  background: transparent;
}

.advup-mais__item--filho {
  padding-top: 6px;
  padding-bottom: 6px;
  padding-left: 42px;
  color: rgb(var(--slate-11));
  font-size: 13px;
}

.advup-mais__item--filho:hover {
  color: rgb(var(--slate-12));
}

/* Os blocos do "Mais" seguem os grupos da barra, com título em frase. */
.advup-mais__bloco + .advup-mais__bloco {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgb(var(--slate-4));
}

.advup-mais__grupo-tit {
  margin: 0;
  padding: 6px 14px 2px;
  color: rgb(var(--slate-11));
  font-size: 12px;
  font-weight: 600;
}

.advup-mais__icone {
  flex: none;
  width: 18px;
  height: 18px;
  color: rgb(var(--slate-11));
}

.advup-mais__rotulo {
  flex: 1;
  min-width: 0;
}

.advup-mais__aviso {
  min-width: 20px;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgb(var(--ruby-9));
  color: rgb(255 255 255);
  font-size: 11px;
  font-weight: 600;
  text-align: center;
}

/* ── etiqueta do ícone ─────────────────────────────────────────────── */
.advup-nav__dica {
  position: fixed;
  z-index: 99999;
  padding: 6px 11px;
  border: 1px solid rgb(255 255 255 / 8%);
  border-radius: 9px;
  background: rgb(2 6 23 / 96%);
  box-shadow: 0 10px 26px -10px rgb(2 6 23 / 80%);
  color: rgb(255 255 255);
  font-size: 12.5px;
  font-weight: 500;
  white-space: nowrap;
  translate: -50% 0;
  pointer-events: none;
}
.advup-dica-enter-active,
.advup-dica-leave-active {
  transition:
    opacity 0.14s ease,
    translate 0.14s cubic-bezier(0.2, 0.9, 0.3, 1);
}
.advup-dica-enter-from,
.advup-dica-leave-to {
  opacity: 0;
  translate: -50% 4px;
}

@media (prefers-reduced-motion: reduce) {
  .advup-dica-enter-active,
  .advup-dica-leave-active,
  .advup-nav__marca,
  .advup-nav__item,
  .advup-nav__rotulo,
  .mais-cel__filhos,
  .mais-cel__item,
  .mais-cel__seta,
  .advup-nav__icone,
  .advup-nav__seta,
  .advup-busca-gatilho,
  .advup-busca-gatilho__lupa,
  .advup-busca-gatilho__lupa-fundo {
    transition: none;
  }
}

/* O gatilho da busca: parece um campo, e não um ícone solto. Quem olha a barra
   precisa entender que dá para DIGITAR ali — a lupa sozinha não dizia isso. */
/* 🔴 NO CELULAR A BUSCA NÃO PODE SUMIR.
   Ao trocar a lupa por um campo, eu escondi o campo abaixo de 640px e o
   telefone ficou SEM nenhuma entrada de busca — medido em 375px: zero. Era
   função que existia e eu tirei. Abaixo de 640px ele volta a ser só o ícone,
   que é o que cabe; o texto e o atalho é que somem, porque não há espaço nem
   teclado para eles. */
/* O vão que sobra entre o logo e os ícones. Ele é que cresce, e o campo fica
   centrado nele — por isso o `ml-auto` da barra de ícones não empurra mais
   nada: já não sobra folga para empurrar. */
.advup-busca-vao {
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  min-width: 0;
}

.advup-busca-gatilho {
  /* Pílula de vidro sobre o topo navy (Victor, 17/09/2026: "mais notável,
     visível, aesthetic"). A lupa ganha o degradê da marca num círculo — é
     ela que o olho acha primeiro —, o texto clareia e o atalho vira tecla. */
  position: relative;
  display: flex;
  gap: var(--esp-2);
  align-items: center;
  flex: 0 1 400px;
  min-width: 0;
  height: 40px;
  padding: 0 var(--esp-2) 0 var(--esp-1);
  border: 1px solid rgb(255 255 255 / 16%);
  border-radius: var(--r-redondo);
  background: linear-gradient(
    135deg,
    rgb(255 255 255 / 11%) 0%,
    rgb(255 255 255 / 4%) 100%
  );
  color: rgb(255 255 255 / 62%);
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 10%),
    0 6px 18px -10px rgb(0 0 0 / 60%);
  cursor: text;
  transition:
    background-color var(--mov-2) var(--mov-curva),
    border-color var(--mov-2) var(--mov-curva),
    box-shadow var(--mov-2) var(--mov-curva),
    color var(--mov-2) var(--mov-curva);
}
.advup-busca-gatilho:hover {
  border-color: rgb(var(--blue-8) / 70%);
  color: rgb(255 255 255 / 85%);
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 14%),
    0 0 0 4px rgb(var(--blue-9) / 16%),
    0 8px 22px -10px rgb(var(--blue-9) / 70%);
}
.advup-busca-gatilho:focus-visible {
  outline: none;
  border-color: rgb(var(--blue-8));
  box-shadow: 0 0 0 4px rgb(var(--blue-9) / 35%);
}
.advup-busca-gatilho__lupa {
  width: 15px;
  height: 15px;
  color: rgb(255 255 255);
  transition: rotate var(--mov-2) var(--mov-curva-pulo);
}
.advup-busca-gatilho__lupa-fundo {
  display: grid;
  place-items: center;
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: var(--r-redondo);
  background: linear-gradient(135deg, rgb(var(--blue-9)), rgb(var(--iris-9)));
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 25%),
    0 3px 10px -3px rgb(var(--blue-9) / 80%);
  transition: scale var(--mov-2) var(--mov-curva-pulo);
}
.advup-busca-gatilho:hover .advup-busca-gatilho__lupa-fundo {
  scale: 1.08;
}
.advup-busca-gatilho:hover .advup-busca-gatilho__lupa {
  rotate: -12deg;
}
.advup-busca-gatilho__txt {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--txt-nota);
  text-align: left;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.advup-busca-gatilho__atalho {
  display: flex;
  gap: 3px;
  flex: none;
}
.advup-busca-gatilho__atalho span {
  display: grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  padding: 0 var(--esp-1);
  border: 1px solid rgb(255 255 255 / 16%);
  border-bottom-width: 2px;
  border-radius: var(--r-2);
  background: rgb(255 255 255 / 7%);
  color: rgb(255 255 255 / 75%);
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  line-height: 1;
}

/* ⚠️ ESTE BLOCO FICA DEPOIS DAS REGRAS BASE, E ISSO NÃO É ESTILO.
   Ele estava ANTES, e `display: none` daqui perdia para o `display: flex`
   escrito mais abaixo — mesma especificidade, quem vem depois ganha. Na tela
   de 375px o resultado era a lupa com os chips ⌘ e K soltos ao lado dela,
   estourando o botão de 40px. É a terceira vez hoje que ordem de regra com a
   mesma especificidade me morde: `.fin-totais--tres` e `.fcli-campo--leitura`
   foram as outras duas. */
@media (max-width: 639px) {
  .advup-busca-gatilho {
    flex: none;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
  }
  .advup-busca-gatilho__txt,
  .advup-busca-gatilho__atalho {
    display: none;
  }
}
</style>
